import React, { useState, useEffect, useCallback } from 'react';
import {
    Divider,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Tab,
    Tabs,
} from '@mui/material';
import {
    Description as FileIcon,
    OpenInNew as OpenInNewIcon,
    Chat as ChatIcon,
    AttachFile as AttachFileIcon,
    Link as LinkIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    Business as BusinessIcon,
    Engineering as EngineeringIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDBadge from 'components/MDBadge';
import MDAvatar from 'components/MDAvatar';
import { getEmailById, getEmailThread, associateEmailThread } from 'services/api/general/email';
import { getProjects, searchProjects } from 'services/api/project';
import { getProductions, searchProductions } from 'services/api/production';
import { formatDate } from 'helpers/dateHelpers';

// Debounce fonksiyonu ekleyelim
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const EmailDetail = ({ email }) => {
    const [currentEmail, setCurrentEmail] = useState(email);
    const [openAssociateDialog, setOpenAssociateDialog] = useState(false);
    const [associationType, setAssociationType] = useState('project');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // Debounce ile arama sorgusu
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    // React Query - Kullan
    const { data: emailData, isLoading: emailLoading } = useQuery({
        queryKey: ['email', email.id],
        queryFn: () => getEmailById(email.id),
        enabled: !!email?.id,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
            if (response.data?.result) {
                setCurrentEmail(response.data.result);
            }
        },
    });

    // Thread'i getir
    const { data: threadData, isLoading: threadLoading } = useQuery({
        queryKey: ['emailThread', email.threadId || email.id],
        queryFn: () => getEmailThread(email.threadId || email.id),
        enabled: !!email.threadId,
        refetchOnWindowFocus: false,
    });

    // Proje aramak için React Query
    const {
        data: projectsData,
        isLoading: projectsLoading,
        refetch: refetchProjects,
    } = useQuery({
        queryKey: ['searchProjectsInEmailDetail', debouncedSearchQuery, page, limit],
        queryFn: async () => {
            try {
                console.log('API call for projects with params:', {
                    searchQuery: debouncedSearchQuery,
                    page,
                    limit,
                });

                // Arama parametrelerini oluştur - boş stringleri undefined ile değiştir
                const params = {
                    searchQuery: debouncedSearchQuery || undefined,
                    page,
                    limit,
                    isActive: true,
                };

                // Arama yapılabilmesi için en az 2 karakter gerekiyor
                if (debouncedSearchQuery && debouncedSearchQuery.length < 2) {
                    // Çok kısa sorgular için tüm projeleri getirelim
                    console.log('Query too short, fetching all active projects');
                    const response = await getProjects('active=true&page=1&limit=10');
                    return response.data;
                }

                console.log('Searching with params:', params);
                const response = await searchProjects(params);
                console.log('Search response:', response.data);
                return response.data;
            } catch (error) {
                console.error('Error searching projects:', error);
                throw error;
            }
        },
        enabled: false, // Manuel olarak tetikleyeceğiz
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: 1, // Hata durumunda sadece bir kez yeniden dene
    });

    // Üretim aramak için React Query
    const {
        data: productionsData,
        isLoading: productionsLoading,
        refetch: refetchProductions,
    } = useQuery({
        queryKey: ['searchProductionsInEmailDetail', debouncedSearchQuery, page, limit],
        queryFn: async () => {
            try {
                console.log('API call for productions with params:', {
                    searchQuery: debouncedSearchQuery,
                    page,
                    limit,
                });

                // Arama parametrelerini oluştur - boş stringleri undefined ile değiştir
                const params = {
                    searchQuery: debouncedSearchQuery || undefined,
                    page,
                    limit,
                    isActive: true,
                };

                // Arama yapılabilmesi için en az 2 karakter gerekiyor
                if (debouncedSearchQuery && debouncedSearchQuery.length < 2) {
                    // Çok kısa sorgular için tüm üretimleri getirelim
                    console.log('Query too short, fetching all active productions');
                    const response = await getProductions('active=true&page=1&limit=10');
                    return response.data;
                }

                console.log('Searching productions with params:', params);
                const response = await searchProductions(params);
                console.log('Search response:', response.data);
                return response.data;
            } catch (error) {
                console.error('Error searching productions:', error);
                throw error;
            }
        },
        enabled: false, // Manuel olarak tetikleyeceğiz
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: 1, // Hata durumunda sadece bir kez yeniden dene
    });

    // Backend'den gelen thread yapısına uygun şekilde veriyi al
    const threadMessages = threadData?.data?.result?.messages || [];
    const isThreadAvailable = threadMessages.length > 1;
    const threadId = email.threadId || email.id;

    // Define search functions
    const handleSearchProjects = useCallback(() => {
        console.log('Searching for projects with:', {
            searchQuery: debouncedSearchQuery,
        });
        setIsSearching(true);
        setTimeout(() => {
            refetchProjects();
        }, 50);
    }, [debouncedSearchQuery, refetchProjects]);

    const handleSearchProductions = useCallback(() => {
        console.log('Searching for productions with:', {
            searchQuery: debouncedSearchQuery,
        });
        setIsSearching(true);
        setTimeout(() => {
            refetchProductions();
        }, 50);
    }, [debouncedSearchQuery, refetchProductions]);

    // Proje arama ve eşleştirme fonksiyonları
    const handleOpenAssociateDialog = () => {
        setOpenAssociateDialog(true);
        // Dialog açıldığında hemen arama yap
        setTimeout(() => {
            if (associationType === 'project') {
                handleSearchProjects();
            } else if (associationType === 'production') {
                handleSearchProductions();
            }
        }, 100);
    };

    // Debounced değer değiştiğinde otomatik arama
    useEffect(() => {
        if (openAssociateDialog) {
            console.log('Debounced query changed, triggering search:', {
                debouncedSearchQuery,
                associationType,
            });
            if (associationType === 'project') {
                handleSearchProjects();
            } else if (associationType === 'production') {
                handleSearchProductions();
            }
        }
    }, [debouncedSearchQuery, openAssociateDialog, associationType, handleSearchProjects, handleSearchProductions]);

    // Association type değiştiğinde yeni arama yap
    useEffect(() => {
        if (openAssociateDialog) {
            if (associationType === 'project') {
                setSearchResults([]);
                handleSearchProjects();
            } else if (associationType === 'production') {
                setSearchResults([]);
                handleSearchProductions();
            }
        }
    }, [associationType, openAssociateDialog, handleSearchProjects, handleSearchProductions]);

    // Arama verilerini izle ve searchResults'ı güncelle
    useEffect(() => {
        if (associationType === 'project' && projectsData?.result) {
            console.log('Setting project search results:', projectsData.result);
            setSearchResults(projectsData.result);
            setIsSearching(false);
        } else if (associationType === 'production' && productionsData?.result) {
            console.log('Setting production search results:', productionsData.result);
            setSearchResults(productionsData.result);
            setIsSearching(false);
        } else if (
            (associationType === 'project' && projectsData) ||
            (associationType === 'production' && productionsData)
        ) {
            console.log('Empty or invalid search results');
            setSearchResults([]);
            setIsSearching(false);
        }
    }, [projectsData, productionsData, associationType]);

    // Gerçek zamanlı arama için aktivite göstergesi
    useEffect(() => {
        if (openAssociateDialog && searchQuery !== debouncedSearchQuery) {
            setIsSearching(true);
        }
    }, [searchQuery, debouncedSearchQuery, openAssociateDialog]);

    const openInGmail = () => {
        const messageId = currentEmail.id;
        // Gmail URL için threadId varsa onu kullan, yoksa messageId
        const threadId = currentEmail.threadId || messageId;

        // Gmail'de maili açma URL'si - gerçek Gmail formatı
        const gmailUrl = `https://mail.google.com/mail/u/0/#inbox/${threadId}`;
        console.log('Gmail URL:', gmailUrl);
        window.open(gmailUrl, '_blank');
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
    };

    const handleCloseAssociateDialog = () => {
        setOpenAssociateDialog(false);
        setSearchQuery('');
        setSearchResults([]);
    };

    const handleAssociationTypeChange = (event, newValue) => {
        setAssociationType(newValue);
    };

    const handleAssociateWithItem = async (itemId) => {
        try {
            // Eşleştirilen öğeyi bul
            const item = searchResults.find((p) => (p._id || p.id) === itemId);

            // Öğe adını belirle (proje veya üretim için farklı field'lar)
            let itemName = 'Seçilen öğe';
            if (associationType === 'project') {
                itemName = item ? item.projectName || 'Seçilen proje' : 'Proje';
            } else if (associationType === 'production') {
                itemName = item ? item.productionName || 'Seçilen üretim' : 'Üretim';
            }

            setIsSearching(true);
            const response = await associateEmailThread(threadId, itemId, associationType);

            if (response.data && response.data.success) {
                // Başarılı eşleştirme mesajı
                alert(`E-posta dizisi başarıyla "${itemName}" ile eşleştirildi.`);
                handleCloseAssociateDialog();
            } else {
                throw new Error(response.data?.message || 'Eşleştirme sırasında bir hata oluştu');
            }
        } catch (error) {
            console.error('Eşleştirme hatası:', error);
            alert(`Eşleştirme hatası: ${error.message}`);
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <MDBox p={3} height="100%" overflow="auto">
            {emailLoading ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
                    <CircularProgress />
                </MDBox>
            ) : (
                <MDBox p={3} borderRadius="xl" bgColor="white" shadow="sm">
                    {/* Email Header */}
                    <MDBox display="flex" justifyContent="space-between" mb={2} alignItems="flex-start">
                        <MDBox display="flex" alignItems="flex-start">
                            <MDAvatar
                                size="md"
                                sx={{
                                    mr: 2,
                                    bgcolor: 'grey.700',
                                    color: 'white',
                                }}
                            >
                                {/[a-zA-Z]/.test(currentEmail.from?.charAt(0))
                                    ? currentEmail.from?.charAt(0).toUpperCase()
                                    : currentEmail.from?.charAt(1).toUpperCase()}
                            </MDAvatar>
                            <MDBox>
                                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                                    {currentEmail.subject}
                                </MDTypography>
                                <MDTypography variant="button" color="text">
                                    From:{' '}
                                    <strong>
                                        {currentEmail.from.split('<')[0].trim() || currentEmail.from}{' '}
                                        <MDTypography
                                            component="span"
                                            variant="caption"
                                            color="text"
                                            fontWeight="regular"
                                        >
                                            {currentEmail.from.includes('<')
                                                ? `(${currentEmail.from.match(/<(.+?)>/)?.[1] || ''})`
                                                : ''}
                                        </MDTypography>
                                    </strong>
                                </MDTypography>
                                {currentEmail.to && (
                                    <MDTypography variant="button" color="text" display="block">
                                        To:{' '}
                                        <strong>
                                            {currentEmail.to.split('<')[0].trim() || currentEmail.to}{' '}
                                            <MDTypography
                                                component="span"
                                                variant="caption"
                                                color="text"
                                                fontWeight="regular"
                                            >
                                                {currentEmail.to.includes('<')
                                                    ? `(${currentEmail.to.match(/<(.+?)>/)?.[1] || ''})`
                                                    : ''}
                                            </MDTypography>
                                        </strong>
                                    </MDTypography>
                                )}
                            </MDBox>
                        </MDBox>
                        <MDBox display="flex" gap={1}>
                            <MDButton
                                variant="outlined"
                                color="dark"
                                size="small"
                                onClick={handleOpenAssociateDialog}
                                startIcon={<LinkIcon />}
                            >
                                Eşleştir
                            </MDButton>
                            <MDButton
                                variant="gradient"
                                color="info"
                                size="small"
                                onClick={openInGmail}
                                startIcon={<OpenInNewIcon />}
                            >
                                Gmail'de Aç
                            </MDButton>
                        </MDBox>
                    </MDBox>

                    <Divider />

                    {/* Email Metadata */}
                    <MDBox display="flex" justifyContent="space-between" my={3} alignItems="center">
                        <MDBox display="flex" gap={1}>
                            {currentEmail.label && (
                                <MDBadge
                                    variant="contained"
                                    color="dark"
                                    size="sm"
                                    badgeContent={currentEmail.label}
                                    container
                                />
                            )}
                            {currentEmail.attachments?.length > 0 && (
                                <MDBadge
                                    variant="contained"
                                    color="dark"
                                    size="sm"
                                    badgeContent={`${currentEmail.attachments.length} ek`}
                                    container
                                />
                            )}
                            {isThreadAvailable && (
                                <MDBadge
                                    variant="contained"
                                    color="info"
                                    size="sm"
                                    badgeContent={`${threadMessages.length} mesaj`}
                                    container
                                    icon={<ChatIcon fontSize="small" />}
                                />
                            )}
                        </MDBox>
                        <MDTypography variant="caption" color="text">
                            {format(new Date(currentEmail.date), 'PPpp')}
                        </MDTypography>
                    </MDBox>

                    {/* Associate Dialog */}
                    <Dialog
                        open={openAssociateDialog}
                        onClose={handleCloseAssociateDialog}
                        fullWidth
                        maxWidth="sm"
                        PaperProps={{
                            sx: {
                                borderRadius: '10px',
                            },
                        }}
                    >
                        <DialogTitle>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDTypography variant="h6">E-posta Dizisini Eşleştir</MDTypography>
                                <IconButton onClick={handleCloseAssociateDialog} size="small">
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </MDBox>
                        </DialogTitle>
                        <DialogContent dividers>
                            <MDBox mb={2}>
                                <Tabs
                                    value={associationType}
                                    onChange={handleAssociationTypeChange}
                                    variant="fullWidth"
                                >
                                    <Tab value="project" label="Proje" icon={<BusinessIcon />} iconPosition="start" />
                                    <Tab
                                        value="production"
                                        label="Üretim"
                                        icon={<EngineeringIcon />}
                                        iconPosition="start"
                                    />
                                    {/* Gelecekte eklenecek diğer eşleştirme tipleri (örn. Lead) */}
                                </Tabs>
                            </MDBox>

                            <MDBox mb={2}>
                                <TextField
                                    fullWidth
                                    label="Proje veya Müşteri Adı"
                                    variant="outlined"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Proje veya müşteri adıyla ara..."
                                    slotProps={{
                                        input: {
                                            endAdornment: searchQuery !== debouncedSearchQuery && (
                                                <InputAdornment position="end">
                                                    <CircularProgress size={20} />
                                                </InputAdornment>
                                            ),
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </MDBox>

                            {isSearching ||
                            (associationType === 'project' && projectsLoading) ||
                            (associationType === 'production' && productionsLoading) ? (
                                <MDBox display="flex" justifyContent="center" py={3}>
                                    <CircularProgress size={30} />
                                </MDBox>
                            ) : searchResults.length > 0 ? (
                                <List sx={{ maxHeight: '300px', overflow: 'auto' }}>
                                    {searchResults.map((item) => {
                                        // Her öğe için başlık ve alt başlık belirle
                                        let primaryText = '';
                                        let secondaryText = '';

                                        if (associationType === 'project') {
                                            primaryText = item.projectName || 'İsimsiz Proje';
                                            secondaryText = (
                                                <>
                                                    <MDTypography variant="caption" display="block">
                                                        Müşteri:{' '}
                                                        {item.aaroCustomerName ||
                                                            item.customer?.name ||
                                                            'Belirtilmemiş'}
                                                    </MDTypography>
                                                </>
                                            );
                                        } else if (associationType === 'production') {
                                            primaryText = item.productionName || 'İsimsiz Üretim';
                                            secondaryText = (
                                                <>
                                                    <MDTypography variant="caption" display="block">
                                                        DekontID: {item?.aaroFields?.DekontID || 'Belirtilmemiş'}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" display="block">
                                                        Tutar:{' '}
                                                        {item?.aaroFields?.Tutar + ' ' + item.aaroFields?.DovizKodu ||
                                                            'Belirtilmemiş'}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" display="block">
                                                        Belge No: {item?.aaroFields?.BelgeNo || 'Belirtilmemiş'}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" display="block">
                                                        Oluşturulma Tarihi: {formatDate(item?.aaroFields?.OlsTar)}
                                                    </MDTypography>
                                                </>
                                            );
                                        }

                                        return (
                                            <ListItem
                                                key={item._id || item.id}
                                                divider
                                                sx={{
                                                    borderRadius: '8px',
                                                    mb: 1,
                                                    '&:hover': { bgcolor: 'grey.100' },
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <MDTypography variant="button" fontWeight="medium">
                                                            {primaryText}
                                                        </MDTypography>
                                                    }
                                                    secondary={secondaryText}
                                                />
                                                <ListItemSecondaryAction>
                                                    <MDButton
                                                        variant="gradient"
                                                        color="success"
                                                        size="small"
                                                        onClick={() => handleAssociateWithItem(item._id || item.id)}
                                                    >
                                                        Eşleştir
                                                    </MDButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            ) : (
                                <MDBox
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    py={3}
                                >
                                    <SearchIcon color="disabled" style={{ fontSize: 48, marginBottom: 16 }} />
                                    <MDTypography variant="body2" color="text" textAlign="center">
                                        {searchQuery
                                            ? 'Arama sonucu bulunamadı.'
                                            : associationType === 'project'
                                            ? 'Proje veya müşteri adı ile arama yapabilirsiniz.'
                                            : 'Üretim veya ürün adı ile arama yapabilirsiniz.'}
                                    </MDTypography>
                                </MDBox>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <MDButton variant="text" color="dark" onClick={handleCloseAssociateDialog}>
                                İptal
                            </MDButton>
                        </DialogActions>
                    </Dialog>

                    {/* Thread Section - Timeline Style */}
                    {isThreadAvailable && (
                        <MDBox mb={3}>
                            <MDTypography variant="button" color="text" fontWeight="medium" mb={2} display="block">
                                E-posta Dizisi ({threadMessages.length} mesaj)
                            </MDTypography>
                            {threadLoading ? (
                                <MDBox display="flex" justifyContent="center" py={2}>
                                    <CircularProgress size={24} />
                                </MDBox>
                            ) : (
                                <MDBox sx={{ position: 'relative', pl: 3 }}>
                                    {/* Sol taraftaki dikey çizgi */}
                                    <MDBox
                                        sx={{
                                            position: 'absolute',
                                            left: '12px',
                                            top: '0',
                                            bottom: '0',
                                            width: '2px',
                                            bgcolor: 'grey.300',
                                            zIndex: 0,
                                        }}
                                    />

                                    {/* Mesajları tarih sırasına göre sırala (eskiden yeniye) */}
                                    {threadMessages
                                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                                        .map((message, index) => (
                                            <MDBox
                                                key={message.id}
                                                p={2}
                                                pl={3}
                                                mb={index < threadMessages.length - 1 ? 2 : 0}
                                                borderRadius="lg"
                                                bgcolor={message.id === currentEmail.id ? 'info.light' : 'grey.100'}
                                                sx={[
                                                    {
                                                        border: '1px solid',
                                                        cursor: 'pointer',
                                                        position: 'relative',
                                                        zIndex: 1,
                                                        '&:hover': {
                                                            boxShadow: 1,
                                                        },
                                                    },
                                                    message.id === currentEmail.id
                                                        ? {
                                                              borderColor: 'info.main',
                                                          }
                                                        : {
                                                              borderColor: 'grey.300',
                                                          },
                                                ]}
                                                onClick={() => openInGmail()}
                                            >
                                                {/* Timeline nokta */}
                                                <MDBox
                                                    sx={[
                                                        {
                                                            position: 'absolute',
                                                            left: '-6px',
                                                            top: '50%',
                                                            width: '12px',
                                                            height: '12px',
                                                            borderRadius: '50%',
                                                            border: '2px solid white',
                                                            transform: 'translateY(-50%)',
                                                            zIndex: 2,
                                                        },
                                                        message.id === currentEmail.id
                                                            ? {
                                                                  bgcolor: 'info.main',
                                                              }
                                                            : {
                                                                  bgcolor: 'grey.500',
                                                              },
                                                    ]}
                                                />

                                                <MDBox display="flex" justifyContent="space-between" mb={1}>
                                                    <MDTypography variant="button" fontWeight="medium">
                                                        {message.from.split('<')[0].trim() || message.from}{' '}
                                                        <MDTypography
                                                            component="span"
                                                            variant="caption"
                                                            color="text"
                                                            fontWeight="regular"
                                                        >
                                                            {message.from.includes('<')
                                                                ? `(${message.from.match(/<(.+?)>/)?.[1] || ''})`
                                                                : ''}
                                                        </MDTypography>
                                                    </MDTypography>
                                                    <MDTypography variant="caption" color="text">
                                                        {format(new Date(message.date), 'PPp')}
                                                    </MDTypography>
                                                </MDBox>
                                                <MDTypography
                                                    variant="body2"
                                                    color="text"
                                                    sx={{
                                                        mb: 1,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {message.snippet || 'İçerik yok.'}
                                                </MDTypography>
                                                <MDBox
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                >
                                                    {message.attachments && message.attachments.length > 0 ? (
                                                        <MDBox display="flex" alignItems="center">
                                                            <AttachFileIcon
                                                                fontSize="small"
                                                                sx={{ mr: 0.5, color: 'text.secondary' }}
                                                            />
                                                            <MDTypography variant="caption" color="text">
                                                                {message.attachments.length} ek
                                                            </MDTypography>
                                                        </MDBox>
                                                    ) : (
                                                        <MDBox />
                                                    )}

                                                    <MDTypography
                                                        variant="caption"
                                                        color="text"
                                                        sx={[
                                                            {
                                                                px: 1,
                                                                py: 0.5,
                                                                borderRadius: '4px',
                                                                fontSize: '0.65rem',
                                                            },
                                                            message.id === currentEmail.id
                                                                ? {
                                                                      backgroundColor: 'info.main',
                                                                  }
                                                                : {
                                                                      backgroundColor: 'grey.300',
                                                                  },
                                                            message.id === currentEmail.id
                                                                ? {
                                                                      color: 'white',
                                                                  }
                                                                : {
                                                                      color: 'text.secondary',
                                                                  },
                                                        ]}
                                                    >
                                                        {index + 1}/{threadMessages.length}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>
                                        ))}
                                </MDBox>
                            )}
                        </MDBox>
                    )}

                    {/* Attachments Section */}
                    {currentEmail.attachments?.length > 0 && (
                        <MDBox mb={3}>
                            <MDTypography variant="button" color="text" fontWeight="medium" mb={2} display="block">
                                Ekler
                            </MDTypography>
                            <MDBox display="flex" gap={2} flexWrap="wrap" p={2} borderRadius="lg" bgcolor="grey.100">
                                {currentEmail.attachments.map((attachment) => (
                                    <MDBox
                                        key={attachment.attachmentId}
                                        display="flex"
                                        alignItems="center"
                                        p={1}
                                        borderRadius="md"
                                        bgcolor="white"
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'grey.200',
                                            minWidth: '200px',
                                            maxWidth: '300px',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: 'grey.50',
                                            },
                                        }}
                                        onClick={openInGmail}
                                    >
                                        <FileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                        <MDBox flex={1} minWidth={0}>
                                            <MDTypography variant="button" color="text" noWrap>
                                                {attachment.filename}
                                            </MDTypography>
                                            <MDTypography variant="caption" color="text" display="block">
                                                {formatFileSize(attachment.size)}
                                            </MDTypography>
                                        </MDBox>
                                        <OpenInNewIcon sx={{ ml: 1, color: 'text.secondary' }} />
                                    </MDBox>
                                ))}
                            </MDBox>
                        </MDBox>
                    )}

                    {/* Email Body - Thread varsa gösterme */}
                    {!isThreadAvailable && (
                        <MDBox
                            mb={3}
                            p={3}
                            bgColor="grey.100"
                            borderRadius="lg"
                            sx={{
                                '& a': { color: 'dark.main' },
                                '& img': { maxWidth: '100%', height: 'auto' },
                            }}
                        >
                            {currentEmail.body ? (
                                <div
                                    dangerouslySetInnerHTML={{ __html: currentEmail.body }}
                                    style={{
                                        wordBreak: 'break-word',
                                        overflow: 'auto',
                                    }}
                                />
                            ) : (
                                <MDTypography variant="body2" color="text">
                                    {currentEmail.snippet || 'No content'}
                                </MDTypography>
                            )}
                        </MDBox>
                    )}
                </MDBox>
            )}
        </MDBox>
    );
};

export default EmailDetail;
